import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import type { Rgb } from '@/lib/ar';
import { toHex } from '@/lib/ar';

import { COMPARE_LIMIT } from './compare';
import type { CalibrationProblem, WhiteBalance } from './kiosk';
import { applyBalance } from './kiosk';
import type { NailArtDesign, Shade } from './schemas';
import { cssColour } from './schemas';
import { TRYON_NAMESPACE } from './strings';

/**
 * The pieces of §6's screen that have no state of their own.
 *
 * **The swatch is the fallback, not the decoration.** Every shade in the
 * strip shows its measured colour as a flat chip beside the overlay. When
 * the overlay is good the chip is a thumbnail; when the device is too slow
 * for the overlay (rule 5) the chip is the whole answer, and it is still a
 * true colour because BP-12 measured it.
 */

/** One shade in the strip. Rule 2's marker rides on it. */
export function ShadeChip({
  shade,
  chosen,
  onPick,
}: {
  shade: Shade;
  chosen: boolean;
  onPick: (skuId: string) => void;
}): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  return (
    <button
      type="button"
      data-testid={`shade-${shade.sku_id}`}
      aria-pressed={chosen}
      onClick={() => {
        onPick(shade.sku_id);
      }}
      className={`flex min-w-24 flex-col items-center gap-1 rounded border p-2 text-xs ${
        chosen ? 'border-primary' : 'border-border'
      }`}
    >
      <span
        aria-hidden="true"
        data-testid={`swatch-${shade.sku_id}`}
        className="h-10 w-10 rounded-full border border-foreground/10"
        style={{ backgroundColor: cssColour(shade) }}
      />
      <span className="font-medium">{shade.shade_name}</span>
      {shade.is_indicative && (
        <span
          data-testid={`indicative-${shade.sku_id}`}
          title={t('shades.indicativeHint')}
          className="rounded bg-accent px-1 text-[10px] text-accent-foreground"
        >
          {t('shades.indicative')}
        </span>
      )}
    </button>
  );
}

/** The 2×2 grid. Empty cells are not drawn: a grey box is not a comparison. */
export function CompareGrid({
  shades,
  onRemove,
}: {
  shades: readonly Shade[];
  onRemove: (skuId: string) => void;
}): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  return (
    <section data-testid="compare" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">{t('compare.heading')}</h2>
      {shades.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="compare-empty">
          {t('compare.empty')}
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-2">
          {shades.map((shade) => (
            <li key={shade.sku_id} className="flex items-center gap-2 rounded border p-2">
              <span
                aria-hidden="true"
                className="h-8 w-8 shrink-0 rounded-full border border-foreground/10"
                style={{ backgroundColor: cssColour(shade) }}
              />
              <span className="flex-1 text-xs">{shade.shade_name}</span>
              <button
                type="button"
                data-testid={`compare-remove-${shade.sku_id}`}
                aria-label={t('compare.remove', { shade: shade.shade_name })}
                className="text-xs text-muted-foreground"
                onClick={() => {
                  onRemove(shade.sku_id);
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        {shades.length >= COMPARE_LIMIT ? t('compare.full') : t('compare.hint')}
      </p>
    </section>
  );
}

/**
 * Rule 2's footer and T12's line, on every render.
 *
 * One component rather than two paragraphs in the screen, so a future
 * layout change cannot drop one of them and leave the other.
 */
export function Disclaimer(): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  return (
    <footer
      data-testid="try-on-disclaimer"
      className="mt-4 flex flex-col gap-1 text-[11px] text-muted-foreground"
    >
      <p>{t('disclaimer.colour')}</p>
      <p>{t('disclaimer.affiliation')}</p>
      <p>{t('disclaimer.onDevice')}</p>
    </footer>
  );
}

/** Rule 5's notice. Shown instead of pretending the overlay is fine. */
export function PhotoModeNotice({ onUsePhoto }: { onUsePhoto: () => void }): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  return (
    <aside role="status" data-testid="try-on-slow" className="rounded bg-accent p-3 text-xs">
      <p className="font-semibold">{t('slow.title')}</p>
      <p>{t('slow.body')}</p>
      {/* BP-22 made this a door rather than an apology: rule 5 says the
          screen "offers photo mode", and until BP-22 there was nowhere to
          send her. */}
      <button
        type="button"
        data-testid="try-on-use-photo"
        className="mt-2 rounded bg-primary px-3 py-1 text-primary-foreground"
        onClick={onUsePhoto}
      >
        {t('photo.tab')}
      </button>
    </aside>
  );
}

/**
 * T7's light check, shown in kiosk mode only.
 *
 * **It shows the correction rather than announcing it.** A calibration that
 * says "done" and nothing else gives whoever ran it no way to tell whether
 * it worked; two swatches — what the camera saw, and what it should have
 * seen — let a person standing at the stand judge it in a second, which is
 * the only review this will ever get.
 */
export function LightCheck({
  sample,
  balance,
  problem,
  onCheck,
}: {
  sample: Rgb | null;
  balance: WhiteBalance | null;
  problem: CalibrationProblem | null;
  onCheck: () => void;
}): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  return (
    <section data-testid="light-check" className="flex flex-col gap-2 rounded border p-3">
      <h2 className="text-sm font-semibold">{t('calibrate.heading')}</h2>
      <p className="text-xs text-muted-foreground">{t('calibrate.hint')}</p>
      <button
        type="button"
        data-testid="light-check-run"
        className="self-start rounded bg-primary px-3 py-1 text-xs text-primary-foreground"
        onClick={onCheck}
      >
        {t('calibrate.action')}
      </button>
      {problem !== null && (
        <p role="status" data-testid={`light-check-${problem}`} className="text-xs text-accent-foreground">
          {t(`calibrate.${problem}`)}
        </p>
      )}
      {sample !== null && balance !== null && (
        <div className="flex items-center gap-3 text-[11px]" data-testid="light-check-result">
          <span className="flex items-center gap-1">
            <span
              aria-hidden="true"
              data-testid="light-check-before"
              className="h-6 w-6 rounded border border-foreground/10"
              style={{ backgroundColor: `#${toHex(sample)}` }}
            />
            {t('calibrate.before')}
          </span>
          <span className="flex items-center gap-1">
            <span
              aria-hidden="true"
              data-testid="light-check-after"
              className="h-6 w-6 rounded border border-foreground/10"
              style={{ backgroundColor: `#${toHex(applyBalance(sample, balance))}` }}
            />
            {t('calibrate.after')}
          </span>
        </div>
      )}
    </section>
  );
}

/**
 * BP-22 §6's two tabs.
 *
 * A pair of buttons and not a dropdown: there are two of them and the
 * client is choosing between "point the camera" and "pick a photograph",
 * which is a decision she makes with her thumb while holding the phone.
 */
export function ModeTabs({
  tab,
  onPick,
}: {
  tab: 'live' | 'photo';
  onPick: (next: 'live' | 'photo') => void;
}): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  return (
    <div className="flex gap-2" role="tablist" data-testid="try-on-tabs">
      {(['live', 'photo'] as const).map((name) => (
        <button
          key={name}
          type="button"
          role="tab"
          aria-selected={tab === name}
          data-testid={`tab-${name}`}
          className={`rounded px-3 py-1 text-sm ${
            tab === name ? 'bg-primary text-primary-foreground' : 'border'
          }`}
          onClick={() => {
            onPick(name);
          }}
        >
          {name === 'live' ? t('photo.liveTab') : t('photo.tab')}
        </button>
      ))}
    </div>
  );
}

/**
 * BP-22 rule 3's catalogue, as a strip beside the shades.
 *
 * The price and the duration are shown because they are what makes a
 * design an add-on rather than a filter: she is choosing something the
 * salon will charge her for, and a strip that hid that would be a strip
 * that surprised her at the counter.
 */
export function DesignStrip({
  designs,
  chosen,
  onPick,
}: {
  designs: readonly NailArtDesign[];
  chosen: string | null;
  onPick: (designId: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  if (designs.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="designs-empty">
        {t('designs.none')}
      </p>
    );
  }
  return (
    <section className="flex flex-col gap-2" data-testid="design-strip">
      <h2 className="text-sm font-semibold">{t('designs.heading')}</h2>
      <div className="flex gap-2 overflow-x-auto">
        <button
          type="button"
          data-testid="design-none"
          aria-pressed={chosen === null}
          className={`min-w-24 rounded border p-2 text-xs ${
            chosen === null ? 'border-primary' : 'border-border'
          }`}
          onClick={() => {
            onPick(null);
          }}
        >
          {t('designs.clear')}
        </button>
        {designs.map((design) => (
          <button
            key={design.id}
            type="button"
            data-testid={`design-${design.id}`}
            aria-pressed={chosen === design.id}
            className={`min-w-24 rounded border p-2 text-xs ${
              chosen === design.id ? 'border-primary' : 'border-border'
            }`}
            onClick={() => {
              onPick(design.id);
            }}
          >
            <span className="block font-medium">{design.name}</span>
            <span className="block text-muted-foreground">
              {t('designs.price', {
                amount: (design.price_paise / 100).toFixed(0),
                minutes: design.duration_min,
              })}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
